import { FunctionComponent, useCallback, useMemo } from 'react';
import {
  ManagedRemoteConfigProvider,
  useProperty,
} from '@spotify-internal/remote-config-integration-react';
import { getPersistedAccessToken } from 'libs/services/auth';
import {
  internalAdmin,
  inProgressFeatures,
  songwriterErrorReporting,
  publisherInviteSongwriter,
  localization,
} from './properties';
import { BrowserResolver } from '@spotify-internal/remote-config-resolver-browser';
import { createBaseTransport, createXResolveProvider } from '@spotify-internal/transport';
import { FetchType } from '@spotify-internal/remote-config-core';

// https://backstage.spotify.net/experimentation/rollout/overview/3959
export const useInternalAdmin = () => useProperty<boolean>(internalAdmin);

// https://backstage.spotify.net/experimentation/rollout/overview/13177
export const useInProgressFeatures = () => useProperty<boolean>(inProgressFeatures);

// https://backstage.spotify.net/experimentation/rollouts/34961
export const useErrorReporting = () => useProperty<boolean>(songwriterErrorReporting);

// https://backstage.spotify.net/experimentation/rollouts/35033
export const usePublisherInviteSongwriter = () => useProperty<boolean>(publisherInviteSongwriter);

// https://backstage.spotify.net/experimentation/rollouts/25521
export const useLocalization = () => useProperty<boolean>(localization);

const RemoteConfig: FunctionComponent<{ username: string }> = ({ username, children }) => {
  const token = useMemo(getPersistedAccessToken, []);

  const createAndConnectTransport = useCallback(() => {
    const transport = createBaseTransport({
      providers: {
        endpoints: createXResolveProvider(),
        token: () =>
          new Promise((resolve) => {
            token && resolve(token);
          }),
      },
    });

    if (transport) {
      transport.connect().then(() => {
        transport.authenticate();
      });
    }
    return transport;
  }, [token]);

  const transport = createAndConnectTransport();

  return (
    <ManagedRemoteConfigProvider
      resolver={BrowserResolver}
      options={{
        clientId: 's4p-webapp',
        timeout: 2000,
        transport: transport,
      }}
      context={{
        username: username,
        fetchType: FetchType.DELAYED,
      }}
    >
      {children}
    </ManagedRemoteConfigProvider>
  );
};

export default RemoteConfig;
